import React, { useState, useEffect, useRef } from 'react';
import { useDigitalLibrary } from '../../contexts/DigitalLibraryContext';
import { 
  FaBook, 
  FaFileAlt, 
  FaGraduationCap, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave,
  FaTimes,
  FaUpload,
  FaSearch,
  FaFilter,
  FaEye,
  FaSort,
  FaChevronUp,
  FaChevronDown
} from 'react-icons/fa';
import './DigitalLibraryAdmin.css';

const DigitalLibraryAdmin = () => {
  const { 
    textbooks,
    examPapers,
    researchPapers,
    loading,
    error,
    uploadResource,
    updateResource,
    deleteResource
  } = useDigitalLibrary();

  // State for resource management
  const [activeTab, setActiveTab] = useState('textbooks');
  const [resources, setResources] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResources, setFilteredResources] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'uploadDate', direction: 'desc' });
  
  // State for modal forms
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentResource, setCurrentResource] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    subject: '',
    description: '',
    university: '',
    year: '',
    language: 'en',
    category: ''
  });
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Update resources when tab changes
  useEffect(() => {
    let resourceData = [];
    switch(activeTab) {
      case 'textbooks':
        resourceData = textbooks;
        break;
      case 'examPapers':
        resourceData = examPapers;
        break;
      case 'researchPapers':
        resourceData = researchPapers;
        break;
      default:
        resourceData = [];
    }
    setResources(resourceData);
  }, [activeTab, textbooks, examPapers, researchPapers]);

  // Update filtered resources when search or resources change
  useEffect(() => {
    let filtered = [...resources];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(resource => 
        resource.title.toLowerCase().includes(query) ||
        resource.author?.toLowerCase().includes(query) ||
        resource.description?.toLowerCase().includes(query) ||
        resource.subject?.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (!a[sortConfig.key] || !b[sortConfig.key]) return 0;
      
      const aValue = typeof a[sortConfig.key] === 'string' ? a[sortConfig.key].toLowerCase() : a[sortConfig.key];
      const bValue = typeof b[sortConfig.key] === 'string' ? b[sortConfig.key].toLowerCase() : b[sortConfig.key];
      
      if (sortConfig.key === 'uploadDate') {
        return sortConfig.direction === 'asc' 
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredResources(filtered);
  }, [resources, searchQuery, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddResource = () => {
    setFormData({
      title: '',
      author: '',
      subject: '',
      description: '',
      university: '',
      year: '',
      language: 'en',
      category: ''
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setShowAddModal(true);
  };

  const handleEditResource = (resource) => {
    setCurrentResource(resource);
    setFormData({
      title: resource.title || '',
      author: resource.author || '',
      subject: resource.subject || '',
      description: resource.description || '',
      university: resource.university || '',
      year: resource.year || '',
      language: resource.language || 'en',
      category: resource.category || ''
    });
    setShowEditModal(true);
  };

  const handleDeleteResource = (resource) => {
    setCurrentResource(resource);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteResource(activeTab, currentResource.id);
      setShowDeleteConfirm(false);
      setCurrentResource(null);
      // Update local resources
      setResources(resources.filter(r => r.id !== currentResource.id));
    } catch (error) {
      console.error("Error deleting resource:", error);
      alert("Failed to delete resource. Please try again.");
    }
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      alert("Please select a file to upload");
      return;
    }
    
    try {
      setUploading(true);
      // Start with a fake progress animation
      let progress = 0;
      const interval = setInterval(() => {
        progress += 5;
        if (progress >= 90) {
          clearInterval(interval);
        }
        setUploadProgress(progress);
      }, 200);
      
      // Upload resource
      const newResourceId = await uploadResource(activeTab, formData, selectedFile);
      
      // Complete progress
      clearInterval(interval);
      setUploadProgress(100);
      
      // Close modal after a delay to show completion
      setTimeout(() => {
        setShowAddModal(false);
        setUploading(false);
        setUploadProgress(0);
        setSelectedFile(null);
      }, 500);
      
    } catch (error) {
      console.error("Error uploading resource:", error);
      setUploading(false);
      alert("Failed to upload resource. Please try again.");
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    try {
      await updateResource(activeTab, currentResource.id, formData);
      setShowEditModal(false);
      
      // Update local resources
      setResources(resources.map(r => 
        r.id === currentResource.id ? { ...r, ...formData } : r
      ));
      
    } catch (error) {
      console.error("Error updating resource:", error);
      alert("Failed to update resource. Please try again.");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="loading">Loading resources...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="digital-library-admin">
      <div className="admin-header">
        <h2>Digital Library Management</h2>
        <button className="add-resource-btn" onClick={handleAddResource}>
          <FaPlus /> Add New Resource
        </button>
      </div>

      <div className="admin-tabs">
        <button 
          className={activeTab === 'textbooks' ? 'active' : ''}
          onClick={() => setActiveTab('textbooks')}
        >
          <FaBook /> Textbooks
        </button>
        <button 
          className={activeTab === 'examPapers' ? 'active' : ''}
          onClick={() => setActiveTab('examPapers')}
        >
          <FaFileAlt /> Exam Papers
        </button>
        <button 
          className={activeTab === 'researchPapers' ? 'active' : ''}
          onClick={() => setActiveTab('researchPapers')}
        >
          <FaGraduationCap /> Research Papers
        </button>
      </div>

      <div className="admin-controls">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>
      </div>

      <div className="resources-table">
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort('title')}>
                Title {sortConfig.key === 'title' && (
                  sortConfig.direction === 'asc' ? <FaChevronUp /> : <FaChevronDown />
                )}
              </th>
              <th onClick={() => handleSort('author')}>
                Author {sortConfig.key === 'author' && (
                  sortConfig.direction === 'asc' ? <FaChevronUp /> : <FaChevronDown />
                )}
              </th>
              <th onClick={() => handleSort('subject')}>
                Subject {sortConfig.key === 'subject' && (
                  sortConfig.direction === 'asc' ? <FaChevronUp /> : <FaChevronDown />
                )}
              </th>
              <th onClick={() => handleSort('uploadDate')}>
                Date Added {sortConfig.key === 'uploadDate' && (
                  sortConfig.direction === 'asc' ? <FaChevronUp /> : <FaChevronDown />
                )}
              </th>
              <th onClick={() => handleSort('downloads')}>
                Downloads {sortConfig.key === 'downloads' && (
                  sortConfig.direction === 'asc' ? <FaChevronUp /> : <FaChevronDown />
                )}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredResources.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-resources">No resources found</td>
              </tr>
            ) : (
              filteredResources.map(resource => (
                <tr key={resource.id}>
                  <td>{resource.title}</td>
                  <td>{resource.author || 'N/A'}</td>
                  <td>{resource.subject || 'N/A'}</td>
                  <td>{formatDate(resource.uploadDate)}</td>
                  <td>{resource.downloads || 0}</td>
                  <td className="actions">
                    <button 
                      className="view-btn" 
                      onClick={() => window.open(resource.fileUrl, '_blank')}
                      title="View Resource"
                    >
                      <FaEye />
                    </button>
                    <button 
                      className="edit-btn" 
                      onClick={() => handleEditResource(resource)}
                      title="Edit Resource"
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="delete-btn" 
                      onClick={() => handleDeleteResource(resource)}
                      title="Delete Resource"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Resource Modal */}
      {showAddModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New {activeTab === 'textbooks' ? 'Textbook' : 
                         activeTab === 'examPapers' ? 'Exam Paper' : 
                         'Research Paper'}</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}><FaTimes /></button>
            </div>
            <form onSubmit={handleSubmitAdd}>
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input 
                  type="text" 
                  id="title" 
                  name="title" 
                  value={formData.title} 
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="author">Author *</label>
                <input 
                  type="text" 
                  id="author" 
                  name="author" 
                  value={formData.author} 
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select 
                    id="subject" 
                    name="subject" 
                    value={formData.subject} 
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Subject</option>
                    <option value="Mathematics">Mathematics</option>
                    <option value="Physics">Physics</option>
                    <option value="Chemistry">Chemistry</option>
                    <option value="Biology">Biology</option>
                    <option value="English">English</option>
                    <option value="Amharic">Amharic</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="university">University</label>
                  <select 
                    id="university" 
                    name="university" 
                    value={formData.university} 
                    onChange={handleFormChange}
                  >
                    <option value="">Select University</option>
                    <option value="AAU">Addis Ababa University</option>
                    <option value="JU">Jimma University</option>
                    <option value="MU">Mekelle University</option>
                    <option value="HU">Hawassa University</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="language">Language *</label>
                  <select 
                    id="language" 
                    name="language" 
                    value={formData.language} 
                    onChange={handleFormChange}
                    required
                  >
                    <option value="en">English</option>
                    <option value="am">Amharic</option>
                    <option value="or">Oromiffa</option>
                    <option value="ti">Tigrinya</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="year">Year</label>
                  <input 
                    type="text" 
                    id="year" 
                    name="year" 
                    value={formData.year} 
                    onChange={handleFormChange}
                    placeholder="YYYY"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <input 
                  type="text" 
                  id="category" 
                  name="category" 
                  value={formData.category} 
                  onChange={handleFormChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea 
                  id="description" 
                  name="description" 
                  value={formData.description} 
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="file">File *</label>
                <div className="file-input-container">
                  <input 
                    type="file" 
                    id="file" 
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="file-input"
                    required
                  />
                  <button 
                    type="button" 
                    className="file-button"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <FaUpload /> Select File
                  </button>
                  <span className="file-name">
                    {selectedFile ? selectedFile.name : 'No file selected'}
                  </span>
                </div>
              </div>

              {uploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span>{uploadProgress}%</span>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={uploading}>
                  <FaSave /> Save Resource
                </button>
                <button 
                  type="button" 
                  className="cancel-btn" 
                  onClick={() => setShowAddModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Resource Modal */}
      {showEditModal && currentResource && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Resource</h3>
              <button className="close-btn" onClick={() => setShowEditModal(false)}><FaTimes /></button>
            </div>
            <form onSubmit={handleSubmitEdit}>
              <div className="form-group">
                <label htmlFor="edit-title">Title *</label>
                <input 
                  type="text" 
                  id="edit-title" 
                  name="title" 
                  value={formData.title} 
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-author">Author *</label>
                <input 
                  type="text" 
                  id="edit-author" 
                  name="author" 
                  value={formData.author} 
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-subject">Subject *</label>
                  <select 
                    id="edit-subject" 
                    name="subject" 
                    value={formData.subject} 
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Subject</option>
                    <option value="Mathematics">Mathematics</option>
                    <option value="Physics">Physics</option>
                    <option value="Chemistry">Chemistry</option>
                    <option value="Biology">Biology</option>
                    <option value="English">English</option>
                    <option value="Amharic">Amharic</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-university">University</label>
                  <select 
                    id="edit-university" 
                    name="university" 
                    value={formData.university} 
                    onChange={handleFormChange}
                  >
                    <option value="">Select University</option>
                    <option value="AAU">Addis Ababa University</option>
                    <option value="JU">Jimma University</option>
                    <option value="MU">Mekelle University</option>
                    <option value="HU">Hawassa University</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-language">Language *</label>
                  <select 
                    id="edit-language" 
                    name="language" 
                    value={formData.language} 
                    onChange={handleFormChange}
                    required
                  >
                    <option value="en">English</option>
                    <option value="am">Amharic</option>
                    <option value="or">Oromiffa</option>
                    <option value="ti">Tigrinya</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-year">Year</label>
                  <input 
                    type="text" 
                    id="edit-year" 
                    name="year" 
                    value={formData.year} 
                    onChange={handleFormChange}
                    placeholder="YYYY"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="edit-category">Category</label>
                <input 
                  type="text" 
                  id="edit-category" 
                  name="category" 
                  value={formData.category} 
                  onChange={handleFormChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-description">Description *</label>
                <textarea 
                  id="edit-description" 
                  name="description" 
                  value={formData.description} 
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-group file-info">
                <label>Current File</label>
                <div className="current-file">
                  <a href={currentResource.fileUrl} target="_blank" rel="noopener noreferrer">
                    View current file
                  </a>
                </div>
                <p className="note">Note: To replace the file, please delete this resource and upload a new one.</p>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  <FaSave /> Update Resource
                </button>
                <button 
                  type="button" 
                  className="cancel-btn" 
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && currentResource && (
        <div className="modal">
          <div className="modal-content delete-confirm">
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button className="close-btn" onClick={() => setShowDeleteConfirm(false)}><FaTimes /></button>
            </div>
            <div className="confirm-message">
              <p>Are you sure you want to delete "<strong>{currentResource.title}</strong>"?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="confirm-actions">
              <button className="delete-confirm-btn" onClick={confirmDelete}>
                Yes, Delete
              </button>
              <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalLibraryAdmin; 